import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from "react-native";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { WorkspaceHeader } from "../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import { useAppConfig } from "../AppConfigProvider";
import CompanionILECardList from "../components/chat-companion/CompanionILECardList/CompanionILECardList";
import CompanionLayout from "../layouts/CompanionLayout";
import type { CompanionILECardProps } from "../components/chat-companion/CompanionILECard/CompanionILECard";
import type { Objective } from "../components/chat-companion/CompanionILECard/CompanionILECard";
import type { RootStackParamList } from "../navigation/navigation.types";
import CustomText from "../components/common/general/CustomText/CustomText";
import { colors } from "../styles/design-system";
import {
  useDeleteIleMutation,
  useGetAllUserIlEsQuery,
  GetAllUserIlEsDocument,
  useResetAiTutorMutation,
} from "@axio/graphql/generated/graphql";
import { AxioActionModal } from "../components/axio-ds/Modal/AxioModal";
import { useAuth } from "../hooks/useAuth";

const stripHtmlTags = (value: string) => value.replace(/<[^>]*>/g, "").trim();

/** ILE item as returned by getAllUserILEs(completionStatus) – activityStatus and objectives[].status included */
type UserILEItem = {
  id: string;
  title: string;
  description?: string | null;
  created?: unknown;
  lastModified?: unknown;
  thumbnail?: string | null;
  activityStatus?: string;
  objectives?: Array<{ id?: string; objective?: string | null; sequence?: number | null; status?: string | null }>;
  courseObjectives?: Array<{
    id?: string;
    objective?: string | null;
    sequence?: number | null;
    status?: string | null;
  }>;
};

const CompanionActivityView = () => {
  const { t } = useTranslation();
  const { primaryColor } = useAppConfig();
  const [activeTab, setActiveTab] = useState("active");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedActivityForDelete, setSelectedActivityForDelete] = useState<{ id: string; title: string } | null>(
    null
  );
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { userInfo } = useAuth();

  const {
    data: dataActive,
    loading: loadingActive,
    error: errorActive,
  } = useGetAllUserIlEsQuery({
    variables: { completionStatus: "active" } as never,
    fetchPolicy: "network-only",
  });
  const {
    data: dataCompleted,
    loading: loadingCompleted,
    error: errorCompleted,
  } = useGetAllUserIlEsQuery({
    variables: { completionStatus: "completed" } as never,
    fetchPolicy: "network-only",
  });

  const [deleteIle, { loading: deletingIle }] = useDeleteIleMutation({
    refetchQueries: [
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "active" } as never },
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "completed" } as never },
    ],
  });
  const [resetAiTutor] = useResetAiTutorMutation({
    refetchQueries: [
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "active" } as never },
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "completed" } as never },
    ],
  });

  const openDeleteModal = useCallback((id: string, title: string) => {
    setSelectedActivityForDelete({ id, title });
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedActivityForDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedActivityForDelete?.id) {
      return;
    }
    try {
      await deleteIle({ variables: { id: selectedActivityForDelete.id } });
    } finally {
      closeDeleteModal();
    }
  }, [closeDeleteModal, deleteIle, selectedActivityForDelete?.id]);

  const handleResetProgress = useCallback(
    async (ileId: string) => {
      if (!userInfo.userId) {
        return;
      }
      await resetAiTutor({
        variables: {
          input: {
            id: ileId,
            userId: userInfo.userId,
          },
        },
      });
    },
    [resetAiTutor, userInfo.userId]
  );

  const ilesToActivities = useCallback(
    (iles: UserILEItem[]): CompanionILECardProps[] =>
      iles.reduce<CompanionILECardProps[]>((acc, ile) => {
        if (!ile?.id || !ile.title) return acc;
        const rawObjectives = ile.objectives ?? ile.courseObjectives ?? [];
        const objectives: Objective[] = rawObjectives.map((obj, index) => {
          const rawStatus = obj?.status?.trim().toLowerCase();
          return {
            id: obj?.id ?? `${ile.id}-${index}`,
            objective: obj?.objective ?? "",
            sequence: obj?.sequence ?? index + 1,
            status: rawStatus === "completed" ? "completed" : undefined,
          };
        });
        const status: CompanionILECardProps["status"] =
          ile.activityStatus === "completed"
            ? "completed"
            : ile.activityStatus === "in_progress"
              ? "in_progress"
              : "not_started";
        const statusLabel =
          status === "completed" ? "Completed" : status === "in_progress" ? "In progress" : "Not started";
        acc.push({
          id: ile.id,
          title: ile.title,
          created: (ile.created as string) ?? (ile.lastModified as string) ?? undefined,
          description: ile.description ? stripHtmlTags(ile.description) : undefined,
          objectives,
          status,
          accessibilityLabel: `${ile.title}, Interactive Learning Experience, ${statusLabel}`,
          onResetProgress: () => {
            void handleResetProgress(ile.id);
          },
          onDeleteActivity: () => openDeleteModal(ile.id, ile.title),
        });
        return acc;
      }, []),
    [handleResetProgress, openDeleteModal]
  );

  const activitiesActive = useMemo(() => {
    const raw = (dataActive?.getAllUserILEs as { data?: UserILEItem[] } | undefined)?.data ?? [];
    return ilesToActivities(raw);
  }, [dataActive?.getAllUserILEs, ilesToActivities]);

  const activitiesCompleted = useMemo(() => {
    const raw = (dataCompleted?.getAllUserILEs as { data?: UserILEItem[] } | undefined)?.data ?? [];
    return ilesToActivities(raw);
  }, [dataCompleted?.getAllUserILEs, ilesToActivities]);

  const activitiesWithNavigationActive = useMemo(
    () =>
      activitiesActive.map((a) => ({
        ...a,
        onPress: () => navigation.navigate("Interactive Learning Environment", { activityId: a.id }),
      })),
    [activitiesActive, navigation]
  );
  const activitiesWithNavigationCompleted = useMemo(
    () =>
      activitiesCompleted.map((a) => ({
        ...a,
        onPress: () => navigation.navigate("Interactive Learning Environment", { activityId: a.id }),
      })),
    [activitiesCompleted, navigation]
  );

  const tabs = useMemo(
    () => [
      { key: "active", title: `${t("Active")} (${activitiesActive.length})` },
      { key: "completed", title: `${t("Completed")} (${activitiesCompleted.length})` },
    ],
    [t, activitiesActive.length, activitiesCompleted.length]
  );

  const displayedActivities =
    activeTab === "active" ? activitiesWithNavigationActive : activitiesWithNavigationCompleted;
  const loading = activeTab === "active" ? loadingActive : loadingCompleted;
  const error = errorActive ?? errorCompleted;

  return (
    <CompanionLayout>
      <View style={styles.container}>
        <WorkspaceHeader title={t("Learning Activities")} headingLevel={1} />
        <View style={styles.tabWrapper}>
          <TabNavigator
            tabData={tabs}
            activeTab={activeTab}
            onTabPress={(key) => {
              setActiveTab(key);
            }}
            activeTabColor={primaryColor}
            useTranslationText={false}
          />
        </View>
        <View style={styles.tabDivider} />
        <ScrollView style={styles.scrollView}>
          <View style={styles.contentContainer}>
            {loading ? (
              <View style={styles.stateContainer}>
                <ActivityIndicator accessibilityLabel="Loading learning activities" color={primaryColor} />
              </View>
            ) : displayedActivities.length > 0 ? (
              <CompanionILECardList activities={displayedActivities} scrollable />
            ) : (
              <View style={styles.stateContainer}>
                <CustomText
                  text={error ? t("Could not load learning activities right now.") : t("No learning activities yet.")}
                  size="m"
                  useTranslationText={false}
                  style={styles.stateText}
                />
              </View>
            )}
          </View>
        </ScrollView>
      </View>
      <AxioActionModal
        visible={deleteModalOpen}
        onRequestClose={closeDeleteModal}
        title={t("Delete learning activity")}
        primaryActionLabel={deletingIle ? t("Deleting...") : t("Delete")}
        onPrimaryAction={() => {
          void handleDeleteConfirm();
        }}
        secondaryActionLabel={t("Cancel")}
        onSecondaryAction={closeDeleteModal}>
        <View style={styles.deleteModalContent}>
          <Text style={styles.deleteModalText}>
            {t(
              `Are you sure you want to delete "${selectedActivityForDelete?.title ?? ""}"? This action cannot be undone.`
            )}
          </Text>
        </View>
      </AxioActionModal>
    </CompanionLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    height: "100%",
    display: "flex",
    // @ts-expect-error - works on web
    maxHeight: "100vh",
    overflow: "hidden",
    backgroundColor: "#f9fafb",
  },
  tabWrapper: {
    marginTop: 24,
    width: "100%",
    maxWidth: 1400,
    marginHorizontal: "auto",
    paddingHorizontal: 16,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  contentContainer: {
    flex: 1,
    width: "100%",
    maxWidth: 1400,
    marginHorizontal: "auto",
  },
  tabDivider: {
    height: 1,
    backgroundColor: "#eaecf0",
    marginHorizontal: 16,
  },
  scrollView: {
    flex: 1,
  },
  stateContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 32,
  },
  stateText: {
    textAlign: "center",
    color: colors.grey[600],
  },
  deleteModalContent: {
    paddingVertical: 8,
  },
  deleteModalText: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.grey[700],
  },
});

export default CompanionActivityView;
